/**
 * Role → grant (BL-1883, ADR 062 — Access Grants § Key Decision 5): the one
 * predicate the route gate's `assertGrant` and the roles help panel both call,
 * plus every grant's plain-English label.
 *
 * Pure and client-bundle-safe (ADR 010 — Isomorphic Services). Kept beside
 * `grants.ts` rather than inside it so the vocabulary file's exports stay
 * exactly as BL-1834 reads them.
 */
import { grantToPermission, isAnyOfGrant, type ActionsFor, type AnyOfGrant, type Grant } from './grants';
import { AUCTIONS_LIST_VIEW_PERMISSIONS, hasAnyPermission, hasPermission } from './permissions';
import type { Role } from './roles';
import { resolveLotEditScope, type LotEditScope } from '@auctogon/core/isomorphic/lifecycle';

/** A grant resource (`auction`, `lot`, …) — one group on the panel. */
export type GrantResource = keyof ActionsFor;

/** A label for every grant. Total by type, so a grant added to `ActionsFor` without one fails tsc (AC 3). */
export type GrantDisplayMap = Record<Grant, string>;

/**
 * Group heading per resource. Key order is display order on the panel.
 */
export const RESOURCE_DISPLAY: Record<GrantResource, string> = {
  auction: 'Auctions',
  lot: 'Lots',
  inventory: 'Inventory',
  file: 'Files',
  customer: 'Customers',
  invoice: 'Invoices',
  // BUG-2186: the page title and the nav entry say "Settlements", so the No access line does too.
  settlement: 'Settlements',
  payment: 'Payments',
  staff: 'Team',
  tenant: 'Company',
  settings: 'Settings',
};

/**
 * One plain-English label per grant. Key order is display order within a group,
 * and the key list is the runtime grant list ({@link ALL_GRANTS}).
 */
export const GRANT_DISPLAY: GrantDisplayMap = {
  'auction.read': 'View auctions',
  'auction.update': 'Create and edit auctions',
  'auction.publish': 'Publish auctions',
  'auction.close': 'Close auctions',
  'auction.close_stage': 'Move auctions through closing stages',
  'auction.approve_bidders': 'Approve bidder registrations',

  'lot.read': 'View lots and bidding',
  'lot.update': 'Edit lots',
  'lot.hammer': 'Hammer lots and bid for buyers',
  'lot.pass': 'Pass lots',
  'lot.withdraw': 'Withdraw lots',
  'lot.sequence': 'Set the lot order',
  'lot.reschedule': 'Change lot closing times',
  'lot.update_accounting': "Edit a lot's tax, load-out fee and title paperwork",
  // Last in the group: where the regrouped `invoice.update` sat before BL-2119.
  'lot.correct_sale': 'Correct sales',

  'inventory.read': 'View inventory',
  'inventory.update': 'Add and edit inventory',

  'invoice.read': 'View invoices',
  'invoice.create': 'Create invoices',
  'invoice.update': 'Edit invoices',
  'invoice.send': 'Send invoices',
  'invoice.void': 'Void invoices',

  'settlement.read': 'View settlements',
  'settlement.update': 'Edit settlements',
  'settlement.adjust': 'Adjust settlements',
  'settlement.send': 'Send statements',

  'payment.read': 'View payments',
  'payment.create': 'Record payments',
  'payment.read_fees': 'View processing fees',

  'customer.read': 'View customers',
  'customer.read_limited': 'Look up customers (read-only)',
  'customer.create': 'Add customers',
  'customer.update': 'Edit customers',
  'customer.set_standing': 'Ban, suspend and reactivate customers',
  'customer.archive': 'Delete and restore customers',
  'customer.read_accounting': "See a customer's accounting",
  // Also admits customer addresses and CRM log entries (BL-1877).
  'customer.update_documents': "Manage a customer's documents, addresses and call log",

  'staff.read': 'View the team',
  'staff.invite': 'Invite team members',
  'staff.update': 'Edit team members and sales reps',
  'staff.grant_role': 'Assign roles',
  'staff.revoke_role': 'Remove team members',

  'tenant.read': 'View company details',
  'tenant.update': 'Edit company details',
  'tenant.publish': 'Publish the website',

  'file.read': 'View files and photos',
  'file.create': 'Upload files and photos',
  'file.delete': 'Delete files and photos',

  'settings.read': 'View settings',
  'settings.update_accounting': 'Change accounting settings',
  'settings.update_auctions': 'Change auction settings',
  'settings.update_website': 'Change website settings',
  'settings.update_support': 'Change support settings',
};

/**
 * Every grant, in label order. Complete because {@link GRANT_DISPLAY} is total
 * over `Grant` (BL-1834 reuses this list).
 */
export const ALL_GRANTS: readonly Grant[] = Object.keys(GRANT_DISPLAY) as Grant[];

const PRJ_76 = { hiddenUntil: 'PRJ-76 — the back office is being rebuilt; invoices, settlements and payments do not exist today' } as const;

/**
 * Grants a role may hold that the panel does not show, each with its reason.
 * A held grant here still passes the gate — this is display only.
 */
export const HIDDEN_GRANTS: Partial<Record<Grant, { readonly hiddenUntil: string }>> = {
  'invoice.read': PRJ_76,
  'invoice.create': PRJ_76,
  'invoice.update': PRJ_76,
  'invoice.send': PRJ_76,
  'invoice.void': PRJ_76,
  'settlement.read': PRJ_76,
  'settlement.update': PRJ_76,
  'settlement.adjust': PRJ_76,
  'settlement.send': PRJ_76,
  // 'payment.read_fees' shows: it gates /legal/fees, the processing-fees page (BL-2371).
};

/**
 * Whether any of `roles` holds `grant` — the predicate the route gate enforces.
 * Today a grant is held through the permission it maps to (the ADR 062 step-1
 * bridge); step 3's `ROLE_GRANTS` replaces only this body.
 *
 * @param roles - the caller's roles at one tenant (additive).
 * @param grant - the grant being checked.
 * @returns `true` when at least one role holds it.
 */
export function rolesHoldGrant(roles: readonly Role[], grant: Grant): boolean {
  return hasPermission([...roles], grantToPermission(grant));
}

/**
 * Whether any of `roles` satisfies a route's grant declaration: the grant
 * itself, or any one grant of an `anyOf` (BL-1833). The one reading of a
 * declaration, shared by the route gate and by a service that gates part of a
 * response on another route's declared grant (BL-2356's offline reference read).
 *
 * @param roles - the caller's roles at one tenant (additive).
 * @param declaration - a real grant or an `anyOf`; `{ none }` and `{ superAdmin }` are the caller's to settle first.
 * @returns `true` when at least one role holds a grant the declaration admits.
 */
export function rolesHoldGrantDeclaration(roles: readonly Role[], declaration: Grant | AnyOfGrant): boolean {
  const grants: readonly Grant[] = isAnyOfGrant(declaration) ? declaration.anyOf : [declaration];
  return grants.some((grant) => rolesHoldGrant(roles, grant));
}

/**
 * BL-2318 — a caller's lot edit scope from the grants they hold: the one mapping
 * the PATCH route (over the caller's roles) and the lot form (over `can()`) share,
 * so the two cannot disagree about who edits which lot fields.
 *
 * @param holds - Whether the caller holds a grant.
 * @param isSuperAdmin - The platform bypass (a super admin holds no tenant roles).
 * @returns `'all'`, `'accounting'`, or `null` when the caller edits no lot field.
 */
export function lotEditScopeForGrants(holds: (grant: Grant) => boolean, isSuperAdmin: boolean): LotEditScope | null {
  return resolveLotEditScope({
    isSuperAdmin,
    holdsFullEdit: holds('inventory.update'),
    holdsAccountingEdit: holds('lot.update_accounting'),
  });
}

/**
 * Every grant `role` holds, in label order.
 *
 * @param role - a role.
 * @returns the grants the gate admits that role for.
 */
export function grantsForRole(role: Role): Grant[] {
  return ALL_GRANTS.filter((grant) => rolesHoldGrant([role], grant));
}

/**
 * The grants the roles panel shows for `role`: {@link grantsForRole} minus {@link HIDDEN_GRANTS}.
 *
 * @param role - a role.
 * @returns the visible held grants, in label order.
 */
export function visibleGrantsForRole(role: Role): Grant[] {
  return grantsForRole(role).filter((grant) => HIDDEN_GRANTS[grant] === undefined);
}

/**
 * The Auctions row for a role that may open the Auctions list without holding an
 * auction grant (a Cataloger, BL-2358). The list gate is a permission set, not a grant.
 */
export const AUCTIONS_LIST_ONLY_LABEL = 'See the Auctions list (list only)';

/** One panel row. `grant` is null for the list-only Auctions row, which no grant backs. */
export interface CapabilityRow {
  grant: Grant | null;
  label: string;
}

/** One panel group: a resource heading and its rows. */
export interface CapabilityGroup {
  resource: string;
  rows: CapabilityRow[];
}

/** What the roles panel renders for one role. */
export interface RoleCapabilities {
  /** Groups with at least one visible held grant, in display order. */
  held: CapabilityGroup[];
  /** Headings of groups the panel offers but this role holds nothing in. */
  notHeld: string[];
}

/** The resource a grant is displayed under: its own. */
function displayResource(grant: Grant): GrantResource {
  return grant.split('.')[0] as GrantResource;
}

/**
 * Group `role`'s visible grants for the roles panel. A group no visible grant
 * belongs to (every grant in it hidden) is neither held nor "not
 * included" — it is not on offer to any role.
 *
 * @param role - a role.
 * @returns the held groups and the not-held group headings.
 */
export function capabilitiesForRole(role: Role): RoleCapabilities {
  const visible = new Set(visibleGrantsForRole(role));
  const held: CapabilityGroup[] = [];
  const notHeld: string[] = [];
  for (const resource of Object.keys(RESOURCE_DISPLAY) as GrantResource[]) {
    const offered = ALL_GRANTS.filter(
      (grant) => displayResource(grant) === resource && HIDDEN_GRANTS[grant] === undefined,
    );
    if (offered.length === 0) continue;
    const rows: CapabilityRow[] = offered
      .filter((grant) => visible.has(grant))
      .map((grant) => ({ grant, label: GRANT_DISPLAY[grant] }));
    // BUG-2575: a role that opens /auctions through the list-view permission set
    // (a Cataloger) holds no auction grant, yet is not "not included".
    if (rows.length === 0 && resource === 'auction' && hasAnyPermission([role], AUCTIONS_LIST_VIEW_PERMISSIONS)) {
      rows.push({ grant: null, label: AUCTIONS_LIST_ONLY_LABEL });
    }
    if (rows.length > 0) held.push({ resource: RESOURCE_DISPLAY[resource], rows });
    else notHeld.push(RESOURCE_DISPLAY[resource]);
  }
  return { held, notHeld };
}
